export const PRICING_SECTION_ID = "pricing";
export const PRICING_SECTION_HREF = `/#${PRICING_SECTION_ID}`;

export type PricingTab =
	| "reach"
	| "growth"
	| "engagement"
	| "editing"
	| "audit"
	| "targeted";

const pricingTabs = new Set<PricingTab>([
	"reach",
	"growth",
	"engagement",
	"editing",
	"audit",
	"targeted",
]);

const legacyTabAliases: Record<string, PricingTab> = {
	views: "reach",
	subscribers: "growth",
	monetization: "engagement",
};

type PricingSearchParams =
	| URLSearchParams
	| Record<string, string | string[] | undefined>;

interface BuildHomePricingHrefOptions {
	tab?: PricingTab | string | null;
	planId?: number | string | null;
	extraParams?: PricingSearchParams;
}

export function normalizePricingTab(value: string | null): PricingTab {
	if (!value) return "reach";
	if (pricingTabs.has(value as PricingTab)) return value as PricingTab;
	return legacyTabAliases[value] ?? "reach";
}

export function parseCheckoutPlanId(value: string | null): number | null {
	if (!value) return null;
	const planId = Number(value);
	if (!Number.isInteger(planId) || planId <= 0) return null;
	return planId;
}

function toUrlSearchParams(
	searchParams?: PricingSearchParams,
): URLSearchParams {
	if (!searchParams) return new URLSearchParams();
	if (searchParams instanceof URLSearchParams) {
		return new URLSearchParams(searchParams);
	}

	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(searchParams)) {
		if (Array.isArray(value)) {
			for (const item of value) {
				params.append(key, item);
			}
			continue;
		}
		if (typeof value === "string") {
			params.set(key, value);
		}
	}
	return params;
}

export function buildHomePricingHref(
	options: BuildHomePricingHrefOptions = {},
): string {
	const params = toUrlSearchParams(options.extraParams);

	if (options.tab) {
		params.set("tab", normalizePricingTab(String(options.tab)));
	}

	if (options.planId !== undefined && options.planId !== null) {
		const planId = parseCheckoutPlanId(String(options.planId));
		if (planId) {
			params.set("plan", String(planId));
		} else {
			params.delete("plan");
		}
	}

	const queryString = params.toString();
	return queryString
		? `/?${queryString}#${PRICING_SECTION_ID}`
		: PRICING_SECTION_HREF;
}

export function buildPricingRedirectHref(
	searchParams: PricingSearchParams = {},
): string {
	const params = toUrlSearchParams(searchParams);
	const tab = params.get("tab");
	const planId = parseCheckoutPlanId(params.get("plan"));

	if (tab) {
		params.set("tab", normalizePricingTab(tab));
	}
	if (params.has("plan")) {
		if (planId) {
			params.set("plan", String(planId));
		} else {
			params.delete("plan");
		}
	}

	return buildHomePricingHref({ extraParams: params });
}
